/** Employee sessions are revoked by the backend; never infer a ban from an ordinary 403. */
export const SESSION_NOTICE_KEY = 'ttmi-employment-access-denied';
export const EMPLOYMENT_ENDED_MESSAGE =
  'Tài khoản đã bị khóa do nhân viên đã nghỉ việc. Vui lòng liên hệ quản trị viên.';
export const SESSION_EXPIRED_MESSAGE = 'Phiên đăng nhập không còn hiệu lực. Vui lòng đăng nhập lại.';

type RecordValue = Record<string, unknown>;
const record = (value: unknown): RecordValue => (value && typeof value === 'object' ? (value as RecordValue) : {});

export function authorizationHeader(headers: unknown): unknown {
  const values = record(headers);
  return typeof values.get === 'function'
    ? values.get.call(headers, 'Authorization')
    : (values.Authorization ?? values.authorization);
}

export function requestToken(headers: unknown): string | null {
  const value = authorizationHeader(headers);
  return typeof value === 'string' ? (/^\s*(?:Token|Bearer)\s+(.+?)\s*$/i.exec(value)?.[1] ?? null) : null;
}

export function isPublicAuthRequest(url = ''): boolean {
  return /(?:^|\/)auth\/(?:token|employee\/(?:login|register))\/?(?:\?|$)|forgot-password|reset-password|register\/employee/.test(
    url
  );
}

function employmentEnded(payload: unknown): boolean {
  const body = record(payload);
  const profile = record(body.data ?? payload);
  const status = typeof profile.status === 'string' ? profile.status.trim().toUpperCase() : '';
  if (status === 'RESIGNED' || status === 'TERMINATED') return true;
  const text = JSON.stringify(payload ?? '').toLowerCase();
  return (
    text.includes('employment_ended') ||
    text.includes('inactive_employee') ||
    text.includes('nhân viên đã nghỉ việc') ||
    text.includes('tài khoản đã bị vô hiệu hóa')
  );
}

export function getSessionNotice(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(SESSION_NOTICE_KEY);
  } catch {
    return null;
  }
}

export function clearSessionNotice(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SESSION_NOTICE_KEY);
  } catch {
    /* The notice is optional. */
  }
}

export class SessionAccessError extends Error {
  constructor(
    public status: number,
    message: string,
    public requestToken: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'SessionAccessError';
  }
}

type Options = {
  profileUrl: string;
  getToken: () => string | null;
  clearSession: () => void | Promise<void>;
  loginPath: string;
  assertProfile?: (payload: unknown) => void;
  now?: () => number;
  onEnded?: (message: string) => void;
};

export function createSessionAccess(options: Options) {
  let verified: { token: string; until: number; profile: unknown } | undefined;
  const pending = new Map<string, Promise<unknown>>();
  const ending = new Set<string>();
  const now = options.now ?? Date.now;

  async function endSession(
    token: string | null,
    message: string,
    onEnded?: (message: string) => void
  ): Promise<boolean> {
    if (!token || options.getToken() !== token) return false;
    if (ending.has(token)) return true;
    ending.add(token);
    try {
      await options.clearSession();
      // Storage cleanup may be asynchronous. A replacement login still wins.
      const current = options.getToken();
      if (current && current !== token) return false;
      verified = undefined;
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(SESSION_NOTICE_KEY, message);
        } catch {
          /* Redirect must still happen. */
        }
      }
      const notify = onEnded ?? options.onEnded;
      if (notify) notify(message);
      else if (typeof window !== 'undefined') window.location.replace(options.loginPath);
      return true;
    } finally {
      ending.delete(token);
    }
  }

  async function handleError(error: unknown, onEnded?: (message: string) => void): Promise<boolean> {
    const original = record(record(error).originalError ?? error);
    const response = record(original.response ?? original);
    const config = record(original.config ?? response.config);
    const url = typeof config.url === 'string' ? config.url : '';
    const base = typeof config.baseURL === 'string' ? config.baseURL : '';
    if (url || base) {
      const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin || 'http://localhost';
      try {
        const requestBase = new URL(base || origin, origin);
        if (new URL(url || '', requestBase).origin !== new URL(options.profileUrl, origin).origin) return false;
      } catch {
        return false;
      }
    }
    const status = response.status;
    let payload = response.data;
    if (typeof Blob !== 'undefined' && payload instanceof Blob) {
      try {
        payload = JSON.parse(await payload.text());
      } catch {
        payload = undefined;
      }
    }
    const denied = employmentEnded(payload);
    if (status !== 401 && !(status === 403 && denied)) return false;
    const token = typeof original.requestToken === 'string' ? original.requestToken : requestToken(config.headers);
    return endSession(token, denied ? EMPLOYMENT_ENDED_MESSAGE : SESSION_EXPIRED_MESSAGE, onEnded);
  }

  async function loadProfile(token: string): Promise<unknown> {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 10_000);
    try {
      const response = await fetch(options.profileUrl, {
        cache: 'no-store',
        redirect: 'error',
        credentials: 'omit',
        headers: { Authorization: `Token ${token}` },
        signal: abort.signal
      });
      let payload: unknown;
      try {
        payload = await response.json();
      } catch (error) {
        if (response.ok) throw error;
      }
      const denied = employmentEnded(payload);
      if (!response.ok || denied) {
        const message = denied ? EMPLOYMENT_ENDED_MESSAGE : 'Không thể xác minh phiên đăng nhập. Vui lòng thử lại.';
        if (response.status === 401 || denied)
          await endSession(token, denied ? EMPLOYMENT_ENDED_MESSAGE : SESSION_EXPIRED_MESSAGE);
        if (denied) options.assertProfile?.(payload);
        throw new SessionAccessError(denied ? 403 : response.status, message, token, payload);
      }
      options.assertProfile?.(payload);
      return payload;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function verify(force = false, explicitToken?: string): Promise<unknown> {
    const token = explicitToken ?? options.getToken();
    if (!token) return undefined;
    if (!force && verified?.token === token && verified.until > now()) return verified.profile;
    let promise = pending.get(token);
    if (!promise) {
      promise = loadProfile(token);
      pending.set(token, promise);
    }
    try {
      const profile = await promise;
      if (!explicitToken && options.getToken() !== token)
        throw new Error('Phiên đăng nhập đã thay đổi. Vui lòng thử lại.');
      if (options.getToken() === token) verified = { token, until: now() + 30_000, profile };
      clearSessionNotice();
      return profile;
    } finally {
      if (pending.get(token) === promise) pending.delete(token);
    }
  }

  function start(): () => void {
    if (typeof window === 'undefined') return () => undefined;
    const refresh = () => {
      void verify(true).catch(() => undefined);
    };
    const visible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const timer = setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', refresh);
    document.addEventListener('visibilitychange', visible);
    refresh();
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', refresh);
      document.removeEventListener('visibilitychange', visible);
    };
  }
  return { verify, handleError, start, endSession };
}
